import api from './api';
import { AxiosResponse } from 'axios';

// Types for Email Template API
export interface EmailTemplateAttachment {
  id: number;
  filename: string;
  content_type: string;
  file_size: number;
  file_url: string | null;
  created_at: string;
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  attachments?: EmailTemplateAttachment[];
}

// Email Template service functions
const emailTemplateService = {
  // Get all email templates
  getAll: async (activeOnly: boolean = false): Promise<EmailTemplate[]> => {
    try {
      const params = activeOnly ? { is_active: true } : {};
      console.log('Fetching all email templates with params:', params);
      const response: AxiosResponse = await api.get('/email-templates/', { params });
      console.log('Email templates fetch response:', response.data);
      
      // Check if the response has the expected format
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        console.warn('Unexpected response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }
  },

  // Get a single email template by ID
  getById: async (id: number): Promise<EmailTemplate> => {
    try {
      if (isNaN(id)) {
        throw new Error(`Invalid template ID: ${id}`);
      }
      
      console.log(`Fetching email template with ID: ${id}`);
      const response: AxiosResponse = await api.get(`/email-templates/${id}/`);
      console.log(`Email template ${id} fetch response:`, response.data);
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching email template with ID ${id}:`, error);
      throw error;
    }
  },

  // Create a new email template
  create: async (template: EmailTemplate): Promise<EmailTemplate> => {
    try {
      console.log('Creating new email template:', template);
      const token = localStorage.getItem('token');
      console.log('Current auth token:', token ? 'Present' : 'Missing');
      
      const response: AxiosResponse = await api.post('/api/email-templates/', template);
      console.log('Email template create response:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating email template:', error);
      throw error;
    }
  },

  // Update an existing email template
  update: async (id: number, template: EmailTemplate): Promise<EmailTemplate> => {
    try {
      if (isNaN(id)) {
        throw new Error(`Invalid template ID: ${id}`);
      }
      
      console.log(`Updating email template with ID: ${id}`, template);
      const response: AxiosResponse = await api.put(`/email-templates/${id}/`, template);
      console.log(`Email template ${id} update response:`, response.data);
      
      return response.data;
    } catch (error) {
      console.error(`Error updating email template with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete an email template
  delete: async (id: number): Promise<void> => {
    try {
      if (isNaN(id)) {
        throw new Error(`Invalid template ID: ${id}`);
      }
      
      console.log(`Deleting email template with ID: ${id}`);
      await api.delete(`/email-templates/${id}/`);
      console.log(`Email template ${id} successfully deleted`);
    } catch (error) {
      console.error(`Error deleting email template with ID ${id}:`, error);
      throw error;
    }
  },

  getEmailTemplates: async (params?: Record<string, any>) => {
    const response = await api.get('/email-templates/', { params });
    return response.data;
  },

  getEmailTemplate: async (id: number) => {
    const response = await api.get(`/email-templates/${id}/`);
    return response.data;
  },

  createEmailTemplate: async (data: any) => {
    const response = await api.post('/email-templates/', data);
    return response.data;
  },

  updateEmailTemplate: async (id: number, data: any) => {
    const response = await api.patch(`/email-templates/${id}/`, data);
    return response.data;
  },

  deleteEmailTemplate: async (id: number) => {
    await api.delete(`/email-templates/${id}/`);
  },

  /** Allowed {{ variable }} names for campaign templates */
  getTemplateVariables: async (): Promise<{ status: string; data: Array<{ name: string; description: string }> }> => {
    const response = await api.get('/email-templates/variables/');
    return response.data;
  },

  validateTemplate: async (subject: string, content: string) => {
    const response = await api.post('/email-templates/validate/', { subject, content });
    return response.data;
  },

  uploadAttachment: async (templateId: number, file: File): Promise<EmailTemplateAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/email-templates/${templateId}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  deleteAttachment: async (templateId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/email-templates/${templateId}/attachments/${attachmentId}/`);
  },
};

export default emailTemplateService; 